import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, getUserDisplayName } from '../firebase';
import { getUserProfile } from '../api/userProfile';
import '../css/Profile.css';

function formatTimestamp(value) {
  if (!value) return '—';
  if (typeof value.toDate === 'function') {
    return value.toDate().toLocaleString();
  }
  if (typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000).toLocaleString();
  }
  return String(value);
}

function formatText(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

function Profile() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        history.replace('/login');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const stored = await getUserProfile(user.uid);
        setProfile({
          uid: stored?.uid || user.uid,
          email: stored?.email || user.email || '',
          displayName: stored?.displayName || getUserDisplayName(user),
          username: stored?.username || '',
          photoUrl: stored?.photoUrl || user.photoURL || '',
          providerId: stored?.providerId || user.providerId || '',
          createdAt: stored?.createdAt || null,
          updatedAt: stored?.updatedAt || null,
          lastLoginAt: stored?.lastLoginAt || null,
          loginCount: stored?.loginCount ?? 0,
          defaultTicker: stored?.preferences?.defaultTicker || '',
          defaultCrypto: stored?.preferences?.defaultCrypto || '',
          hasFirestoreDoc: Boolean(stored),
        });
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError(err.message || 'Failed to load profile information');
      } finally {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, [history]);

  if (isLoading) {
    return (
      <div className="profile-page">
        <h1>Profile Information</h1>
        <p className="profile-status">Loading profile…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <h1>Profile Information</h1>
        <p className="profile-error">{error}</p>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const rows = [
    { label: 'Display name', value: formatText(profile.displayName) },
    { label: 'Username', value: formatText(profile.username) },
    { label: 'Email', value: formatText(profile.email) },
    { label: 'User ID', value: formatText(profile.uid) },
    { label: 'Provider', value: formatText(profile.providerId) },
    { label: 'Created', value: formatTimestamp(profile.createdAt) },
    { label: 'Last updated', value: formatTimestamp(profile.updatedAt) },
    { label: 'Last login', value: formatTimestamp(profile.lastLoginAt) },
    { label: 'Login count', value: formatText(profile.loginCount) },
    { label: 'Default ticker', value: formatText(profile.defaultTicker) },
    { label: 'Default crypto', value: formatText(profile.defaultCrypto) },
  ];

  return (
    <div className="profile-page">
      <h1>Profile Information</h1>
      <div className="profile-card">
        {profile.photoUrl ? (
          <img
            className="profile-photo"
            src={profile.photoUrl}
            alt={profile.displayName}
          />
        ) : null}
        <dl className="profile-fields">
          {rows.map((row) => (
            <div className="profile-row" key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
        {!profile.hasFirestoreDoc && (
          <p className="profile-status">
            No saved Firestore profile was found. Sign out and sign back in to create one.
          </p>
        )}
      </div>
    </div>
  );
}

export default Profile;
